import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_URL = `${API_BASE_URL}/api/asset-user`;

const getAuthHeader = () => {
  const token = sessionStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`
  };
};

// Asset User: Get all assets in the system
export const getAllAssets = async () => {
  try {
    const response = await axios.get(`${API_URL}/all-assets`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset User: Get dashboard stats
export const getDashboardStats = async () => {
  try {
    const response = await axios.get(`${API_URL}/dashboard-stats`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset User: Get locations (labs/departments) from DB
export const getLocations = async () => {
  try {
    const response = await axios.get(`${API_URL}/locations`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset User: Get available assets
export const getAvailableAssets = async (labId = null) => {
  try {
    const params = labId ? { labId } : {};
    const response = await axios.get(`${API_URL}/available`, {
      headers: getAuthHeader(),
      params
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset User: Get asset stock information
export const getAssetStock = async (assetId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/assets/${assetId}/stock`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset User: Get my assigned assets
export const getMyAssets = async () => {
  try {
    const response = await axios.get(`${API_URL}/my-assets`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset User: Create asset request
export const createAssetRequest = async (data) => {
  try {
    const response = await axios.post(`${API_URL}/request`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset User: Get my requests
export const getMyRequests = async (status = null) => {
  try {
    const params = status ? { status } : {};
    const response = await axios.get(`${API_URL}/my-requests`, {
      headers: getAuthHeader(),
      params
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset User: Update own pending request
export const updateAssetRequest = async (requestId, data) => {
  try {
    const response = await axios.put(`${API_URL}/request/${requestId}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset User: Delete own pending request
export const deleteAssetRequest = async (requestId) => {
  try {
    const response = await axios.delete(`${API_URL}/request/${requestId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset Incharge: Get pending requests
export const getPendingRequests = async (labId = null, status = 'pending') => {
  try {
    const params = {};
    if (labId) params.labId = labId;
    if (status) params.status = status;

    const response = await axios.get(`${API_URL}/requests/pending`, {
      headers: getAuthHeader(),
      params
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset Incharge: Approve request
export const approveAssetRequest = async (requestId, data) => {
  try {
    const response = await axios.post(`${API_URL}/requests/${requestId}/approve`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset Incharge: Reject request
export const rejectAssetRequest = async (requestId, data) => {
  try {
    const response = await axios.post(`${API_URL}/requests/${requestId}/reject`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset Incharge: Get active assignments
export const getActiveAssignments = async (labId = null) => {
  try {
    const params = labId ? { labId } : {};
    const response = await axios.get(`${API_URL}/assignments/active`, {
      headers: getAuthHeader(),
      params
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset Incharge: Revoke asset
export const revokeAsset = async (assignmentId, data = {}) => {
  try {
    const response = await axios.post(`${API_URL}/assignments/${assignmentId}/revoke`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset Incharge: Swap assets
export const swapAssets = async (data) => {
  try {
    const response = await axios.post(`${API_URL}/swap`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};
// Asset Incharge: Get breakdown requests
export const getBreakdownRequests = async (labId = null, status = 'pending') => {
  try {
    const params = {
      requestType: 'asset_breakdown',
      status
    };
    if (labId) params.labId = labId;

    const response = await axios.get(`${API_URL}/requests/pending`, {
      headers: getAuthHeader(),
      params
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Asset Incharge: Get extension/release requests
export const getOtherRequests = async (labId = null, status = 'pending') => {
  try {
    const params = {
      status
    };
    if (labId) params.labId = labId;

    const response = await axios.get(`${API_URL}/requests/pending`, {
      headers: getAuthHeader(),
      params
    });
    
    // Filter to get only extension and release requests
    const data = response.data;
    data.requests = data.requests.filter(r => 
      r.requestType === 'asset_extension' || r.requestType === 'asset_release'
    );
    data.total = data.requests.length;
    return data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

const assetUserService = {
  getAllAssets,
  getDashboardStats,
  getLocations,
  getAvailableAssets,
  getAssetStock,
  getMyAssets,
  createAssetRequest,
  getMyRequests,
  updateAssetRequest,
  deleteAssetRequest,
  getPendingRequests,
  approveAssetRequest,
  rejectAssetRequest,
  getActiveAssignments,
  revokeAsset,
  swapAssets,
  getBreakdownRequests,
  getOtherRequests
};

export default assetUserService;